import { createContext, type ReactNode, useContext, useEffect } from "react";
import { Loader } from "@/components/loader";
import { config, PROOFING_FLOW_APP_ID } from "@/config";
import { useGlobalPermissions } from "@/domain/usersAndProjects/queryHooks";
import { handleAuthError, isAuthError } from "@/lib/auth/authErrorHandler";
import { useFetch } from "@/lib/react-query/hooks";
import { userQueryKeys } from "@/lib/react-query/keyFactory";
import type { AuthContextType, User, UserGroup } from "./authProvider.types";
import { useConfigCatFlags } from "./useConfigCatFlags";

export { useFeatureFlags } from "./useFeatureFlags";

const AuthContext = createContext<AuthContextType | null>(null);

interface Props {
  children: ReactNode;
}
const authServiceUrl = config.auth.serviceUrl;
const userUrl = `${authServiceUrl}/api/users/me`;
export function AuthProvider({ children }: Props) {
  const { data, error, isLoading } = useFetch<{ user: User | null; groups: UserGroup[] | null }>({
    url: userUrl,
    queryKey: userQueryKeys.detail("me"),
  });

  const user = data?.user ?? null;
  const supplierGroups = (
    data?.groups?.filter((g) => {
      const hasAppSupplierRole = g.appRoles.some(
        (ar) => ar.appId === PROOFING_FLOW_APP_ID && ar.roleName === "supplier",
      );
      const hasProjectSupplierRole = g.projectRoles.some(
        (pr) => pr.appId === PROOFING_FLOW_APP_ID && pr.roleName === "supplier",
      );
      return hasAppSupplierRole || hasProjectSupplierRole;
    }) || []
  ).map((g) => g.id);
  // Fetch global permissions (only when user is authenticated)
  const { permissions } = useGlobalPermissions({ enabled: !!user });

  // Fetch feature flags from ConfigCat (only when user is authenticated)
  const featureFlags = useConfigCatFlags({
    user,
    sdkKey: config.configCat.sdkKey,
  });

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch authenticated user:", error);

      // Handle authentication errors (401) by redirecting to login
      // Note: Also treating 500 errors from auth service as auth failures (may need revisiting)
      const shouldRedirectToLogin = isAuthError(error) || error.message?.includes("500");

      if (shouldRedirectToLogin) {
        handleAuthError();
      }
    }
  }, [error]);

  // Block child rendering until auth status is resolved.
  // This prevents downstream components from firing API calls before
  // we know if the user is authenticated, avoiding spurious 500 errors.
  if (isLoading) {
    return <Loader />;
  }

  const authContext: AuthContextType = { user, permissions, featureFlags, supplierGroups };

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
